/** Attributes — CRUD атрибутов с values (Phase B2). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Layers, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

interface AttrValue {
  id: number;
  slug: string;
  label: string;
  sort_order: number;
}
interface Attribute {
  id: number;
  slug: string;
  name: string;
  input_type: "dropdown" | "multiselect" | "text" | "numeric" | "boolean";
  unit: string | null;
  is_filterable: boolean;
  is_visible: boolean;
  values: AttrValue[];
}

const INPUT_TYPE_LABELS: Record<Attribute["input_type"], string> = {
  dropdown: "Один из списка",
  multiselect: "Несколько из списка",
  text: "Текст",
  numeric: "Число",
  boolean: "Да/Нет",
};

const EMPTY_FORM = {
  slug: "",
  name: "",
  input_type: "dropdown" as Attribute["input_type"],
  unit: "",
  is_filterable: true,
  is_visible: true,
};

export function AttributesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | "new" | null>(null);

  const { data: attrs, isLoading } = useQuery({
    queryKey: ["attributes"],
    queryFn: async () => {
      const { data } = await api.get("/attributes");
      return data as Attribute[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/attributes/${id}`);
    },
    onSuccess: () => {
      toast.success("Атрибут удалён");
      queryClient.invalidateQueries({ queryKey: ["attributes"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Не удалось удалить"),
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<number>({
    onConfirm: (id) => deleteMutation.mutateAsync(id),
    title: "Удалить атрибут?",
  });

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Конфигурация
          </Link>
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading flex items-center gap-2">
            <Layers className="w-6 h-6" />
            Атрибуты
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Характеристики товаров (цвет, материал, габариты). Для select-типов
            добавляются values. Атрибут подключается к Product Type.
          </p>
        </div>
        <Button onClick={() => setEditingId("new")}>
          <Plus className="w-4 h-4 mr-1" />
          Создать атрибут
        </Button>
      </div>

      {editingId === "new" && (
        <AttributeForm
          mode="create"
          initial={EMPTY_FORM}
          onClose={() => setEditingId(null)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {attrs?.map((a) => (
          <Card key={a.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-base">{a.name}</CardTitle>
                  <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                    {a.slug}
                  </div>
                </div>
                <Badge variant="secondary" className="text-[10px]">
                  {INPUT_TYPE_LABELS[a.input_type]}
                  {a.unit ? ` · ${a.unit}` : ""}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex gap-3 text-xs text-muted-foreground">
                {a.is_filterable && <span>✓ фильтрация</span>}
                {a.is_visible && <span>✓ показывать на фронте</span>}
              </div>

              {(a.input_type === "dropdown" || a.input_type === "multiselect") && (
                <AttributeValuesEditor attribute={a} />
              )}

              {editingId === a.id ? (
                <AttributeForm
                  mode="edit"
                  attribute={a}
                  initial={{
                    slug: a.slug,
                    name: a.name,
                    input_type: a.input_type,
                    unit: a.unit ?? "",
                    is_filterable: a.is_filterable,
                    is_visible: a.is_visible,
                  }}
                  onClose={() => setEditingId(null)}
                />
              ) : (
                <div className="flex items-center justify-between pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingId(a.id)}
                  >
                    Редактировать
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => askDelete(a.id, a.name)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      {deleteDialog}
    </div>
  );
}

function AttributeForm({
  mode,
  attribute,
  initial,
  onClose,
}: {
  mode: "create" | "edit";
  attribute?: Attribute;
  initial: typeof EMPTY_FORM;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(initial);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        unit: form.unit.trim() || null,
      };
      if (mode === "create") {
        await api.post("/attributes", payload);
      } else if (attribute) {
        await api.patch(`/attributes/${attribute.id}`, payload);
      }
    },
    onSuccess: () => {
      toast.success(mode === "create" ? "Создан" : "Сохранено");
      queryClient.invalidateQueries({ queryKey: ["attributes"] });
      onClose();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail ?? "Ошибка сохранения"),
  });

  return (
    <Card
      className={mode === "edit" ? "mt-3 border-primary/30" : undefined}
    >
      {mode === "create" && (
        <CardHeader>
          <CardTitle className="text-base">Новый атрибут</CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-3 pt-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Slug *</Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="color"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Название *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Цвет"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Тип ввода</Label>
            <Select
              value={form.input_type}
              onValueChange={(v: any) => setForm({ ...form, input_type: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(INPUT_TYPE_LABELS).map(([k, l]) => (
                  <SelectItem key={k} value={k}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Единица (опционально)</Label>
            <Input
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              placeholder="см, кг, Вт"
            />
          </div>
        </div>
        <div className="flex items-center gap-4 pt-1">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={form.is_filterable}
              onChange={(e) =>
                setForm({
                  ...form,
                  is_filterable: (e.target as HTMLInputElement).checked,
                })
              }
            />
            Фильтрация в каталоге
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={form.is_visible}
              onChange={(e) =>
                setForm({
                  ...form,
                  is_visible: (e.target as HTMLInputElement).checked,
                })
              }
            />
            Показывать на фронте
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={
              !form.slug.trim() || !form.name.trim() || saveMutation.isPending
            }
          >
            {mode === "create" ? "Создать" : "Сохранить"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AttributeValuesEditor({ attribute }: { attribute: Attribute }): JSX.Element {
  const queryClient = useQueryClient();
  const [newSlug, setNewSlug] = useState("");
  const [newLabel, setNewLabel] = useState("");

  const addMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/attributes/${attribute.id}/values`, {
        slug: newSlug.trim(),
        label: newLabel.trim(),
        sort_order: attribute.values.length,
      });
    },
    onSuccess: () => {
      setNewSlug("");
      setNewLabel("");
      queryClient.invalidateQueries({ queryKey: ["attributes"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  const removeMutation = useMutation({
    mutationFn: async (valueId: number) => {
      await api.delete(`/attributes/values/${valueId}`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["attributes"] }),
  });

  return (
    <div className="space-y-2 border-t pt-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">
        Values ({attribute.values.length})
      </div>
      <div className="flex flex-wrap gap-1.5">
        {attribute.values.map((v) => (
          <Badge
            key={v.id}
            variant="outline"
            className="gap-1 pr-1"
          >
            {v.label}
            <button
              type="button"
              onClick={() => removeMutation.mutate(v.id)}
              className="text-muted-foreground hover:text-destructive"
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2 items-end pt-2">
        <div className="flex-1 space-y-1">
          <Label className="text-xs">Slug</Label>
          <Input
            className="h-8 text-xs"
            value={newSlug}
            onChange={(e) => setNewSlug(e.target.value)}
            placeholder="beige"
          />
        </div>
        <div className="flex-1 space-y-1">
          <Label className="text-xs">Label</Label>
          <Input
            className="h-8 text-xs"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Бежевый"
          />
        </div>
        <Button
          size="sm"
          onClick={() => addMutation.mutate()}
          disabled={!newSlug.trim() || !newLabel.trim()}
        >
          +
        </Button>
      </div>
    </div>
  );
}
